import type { ReactElement } from "react";
import { useCallback, useId, useMemo, useState } from "react";

import { Combobox, DropdownButton } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type { TranslationReplacements } from "app/filters/i18nFilter";
import { ScrollDismissBoundary } from "app/pages/sales-intelligence/common-components/scroll-dismiss/ScrollDismissBoundary";
import type { Translatable } from "app/pages/sales-intelligence/types";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

import { DROPDOWN_BUTTON_TEXT_LENGTH_LIMIT } from "../../constants/common";
import { getSeparatedValuesPlusN } from "../../helpers/common";

import { StyledBaseDropdownButtonWrap, StyledDropdownContainer } from "./styles";

export type EmployeeProfileDropdownProps = {
    id: string;
    items: readonly string[];
    selectedIds?: readonly string[];
    emptyButtonText?: Translatable;
    width?: string;
    onAdd: (id: string) => void;
    onRemove: (id: string) => void;
    onToggle?: (isOpen: boolean) => void;
};

const EmployeeProfileDropdown = ({
    id,
    items,
    selectedIds = [],
    emptyButtonText,
    width = "366px",
    onAdd,
    onRemove,
    onToggle,
}: EmployeeProfileDropdownProps): ReactElement => {
    const translate = useTranslation();
    const containerId = useId();
    const [isOpen, setIsOpen] = useState(false);
    const isEmpty = selectedIds.length === 0;

    const buttonText = useMemo(() => {
        if (selectedIds.length === 0) {
            if (typeof emptyButtonText !== "undefined") {
                return translate(emptyButtonText.key, emptyButtonText.replacements as TranslationReplacements);
            }

            return translate(`si.contacts-search-profiles.${id}-dropdown.no-items`);
        }

        if (selectedIds.length === items.length) {
            return translate(`si.contacts-search-profiles.${id}-dropdown.all-items`);
        }

        return getSeparatedValuesPlusN(selectedIds, DROPDOWN_BUTTON_TEXT_LENGTH_LIMIT);
    }, [selectedIds, items.length, emptyButtonText, id, translate]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            setIsOpen(open);
            onToggle?.(open);
        },
        [onToggle],
    );

    const handleSelect = useCallback(
        (itemId: string) => {
            if (selectedIds.includes(itemId)) {
                onRemove(itemId);
            } else {
                onAdd(itemId);
            }
        },
        [selectedIds, onAdd, onRemove],
    );

    return (
        <StyledDropdownContainer
            id={containerId}
            $width={width}
        >
            <ScrollDismissBoundary
                isOpen={isOpen}
                onClose={() => handleOpenChange(false)}
            >
                <Combobox
                    isOpen={isOpen}
                    onOpenChange={handleOpenChange}
                    placement="bottom-start"
                    matchTriggerWidth
                    data-automation={`employee-profile-${id}-dropdown`}
                    data-react-aria-top-layer="true"
                >
                    <Combobox.Trigger>
                        <StyledBaseDropdownButtonWrap isEmpty={isEmpty}>
                            <DropdownButton>{buttonText}</DropdownButton>
                        </StyledBaseDropdownButtonWrap>
                    </Combobox.Trigger>

                    <Combobox.Content>
                        {items.map((item) => (
                            <Combobox.Option
                                key={item}
                                id={item}
                                textValue={item}
                                onClick={() => handleSelect(item)}
                            >
                                <Flex
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap="8px"
                                    width="100%"
                                >
                                    <Combobox.OptionLabel>{item}</Combobox.OptionLabel>
                                    <Combobox.OptionCheckbox isChecked={selectedIds.includes(item)} />
                                </Flex>
                            </Combobox.Option>
                        ))}
                    </Combobox.Content>
                </Combobox>
            </ScrollDismissBoundary>
        </StyledDropdownContainer>
    );
};

export default EmployeeProfileDropdown;
